import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { Observable, of, throwError } from 'rxjs';


import { UserControllerService, ProjectControllerService } from '@rapider/sdk';
import { NotificationService } from '@rapider/services';
import { AuthStore } from '@rapider/authentication';

import { extractPersonId, WORKSPACE_CONFIG } from './workspace.config';

/**
 * @class WorkspaceFlowService
 * @description Manages the complex multi-step authentication and workspace resolution flow.
 */
@Injectable({ providedIn: 'root' })
export class WorkspaceFlowService {

  private readonly userController = inject(UserControllerService);
  private readonly projectController = inject(ProjectControllerService);
  private readonly router = inject(Router);
  private readonly notificationService = inject(NotificationService);
  private readonly authStore = inject(AuthStore);


  /**
   * @method handlePostLoginFlow
   * @description Executes Steps 2 through 4 of the authentication flow.
   * Fetches user -> Changes active person -> Saves new token -> Resolves Workspace.
   * * @param {string} initialToken - The token received immediately after `/users/login`.
   * @returns {Observable<void>} Observable that completes when the flow is finished.
   */
  public handlePostLoginFlow(initialToken: string): Observable<void> {
    // Temporarily set the initial token so the HTTP interceptor can use it for the next call
    localStorage.setItem('Authorization', initialToken);

    return this.userController.getUserByAuthenticationToken().pipe(
      switchMap((user: any) => {
        const personId = extractPersonId(user);
        if (!personId) {
          return throwError(() => new Error('No person profile found for user.'));
        }
        // Step 3: Change active person
        return this.userController.changeActivePerson({ body: { personId } });
      }),
      tap((response: any) => {
        // Save the new, fully-authorized Person Token
        if (response && response.authenticationToken) {
          localStorage.setItem('Authorization', response.authenticationToken);
        }
      }),
      switchMap(() => {
        // Use AuthStore to resolve workspace
        this.authStore.resolveWorkspaceFlow({});
        return of(void 0);
      }),
      catchError((error) => {
        this.notificationService.createNotification('error', 'Error', WORKSPACE_CONFIG.notifications.authFailed);
        this.router.navigate(['/login']);
        return of(undefined);
      })
    );
  }

  /**
   * @method resolveActiveWorkspace
   * @description Checks local storage for an active project. If found, fetches it.
   * If not, fetches recent projects and navigates the user to selection or creation.
   * * @returns {Observable<void>} Observable stream of the resolution process.
   */
  // Workspace resolution is now handled by AuthStore.resolveWorkspaceFlow

  /**
   * @method selectWorkspace
   * @description Commits the user's workspace selection to state/storage and navigates to the dashboard.
   * * @param {any} project - The selected project data.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  public selectWorkspace(project: any): void {
    // Use AuthStore to set the active workspace
    this.authStore.setActiveWorkspace({ project });
  }
}